import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { apiClient } from "../utils/api";
import { useAuth } from "./AuthContext";

const TenantContext = createContext(null);

const LOGIN_BG_CACHE_KEY = "login_background_url_v1";

const DEFAULT_BRANDING = {
  logo_url: null,
  primary_color: "#1B3F8F",
  secondary_color: "#5C6670",
  company_name: "SalesTracker",
  login_background_url: null,
  name: ""
};

export function TenantProvider({ children }) {
  const { user, token } = useAuth();
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loaded, setLoaded] = useState(false);

  const fetchBranding = useCallback(async () => {
    if (!token) return;
    try {
      const res = await apiClient.get("/tenant/branding");
      const data = res.data;
      setBranding({
        logo_url: data.logo_url || null,
        primary_color: data.primary_color || "#1B3F8F",
        secondary_color: data.secondary_color || "#5C6670",
        company_name: data.company_name || data.name || "SalesTracker",
        login_background_url: data.login_background_url || null,
        name: data.name || ""
      });
      // Keep login background cache in sync so LoginPage renders it instantly
      try {
        if (data.login_background_url) {
          localStorage.setItem(LOGIN_BG_CACHE_KEY, data.login_background_url);
        } else {
          localStorage.removeItem(LOGIN_BG_CACHE_KEY);
        }
      } catch {
        // ignore
      }
    } catch {
      // Keep defaults
    } finally {
      setLoaded(true);
    }
  }, [token]);

  useEffect(() => {
    if (user && token) {
      fetchBranding();
    } else {
      setBranding(DEFAULT_BRANDING);
      setLoaded(false);
    }
  }, [user, token, fetchBranding]);

  // Apply CSS variables for dynamic theming
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--color-primary", branding.primary_color);
    root.style.setProperty("--color-secondary", branding.secondary_color);
    
    // Calculate hover/light variants
    const hex = branding.primary_color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    root.style.setProperty("--color-primary-rgb", `${r}, ${g}, ${b}`);
  }, [branding]);

  return (
    <TenantContext.Provider value={{ branding, loaded, refetchBranding: fetchBranding }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) return { branding: DEFAULT_BRANDING, loaded: false, refetchBranding: () => {} };
  return ctx;
}
